import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { trackFathomEvent } from '../helpers/fathom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { useHistory, useLocation } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { db, functions } from '../helpers/firebase'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import Brightness4Icon from '@material-ui/icons/Brightness4'
import Brightness7Icon from '@material-ui/icons/Brightness7'
import {
  UNIVERSITY_SUNSET_MESSAGE,
  UNSUPPORTED_ROLE_MESSAGE,
  isUniversityRole,
  routeForRole
} from '../constants/roles'
import { rememberParentInviteToken } from '../helpers/parentInviteStorage'
import BetaVersionBadge from './UiElements/BetaVersionBadge'
import './Home.css'
import '../styles/dynamic-background.css'

const getTimeBucket = (ms) => {
  const seconds = ms / 1000
  if (seconds < 30) return 'home_time_0_30s'
  if (seconds < 60) return 'home_time_30_60s'
  if (seconds < 300) return 'home_time_1_5m'
  return 'home_time_5m_plus'
}

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme()
  return (
    <button
      className='qn-theme-toggle'
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? <Brightness4Icon style={{ fontSize: 20 }} /> : <Brightness7Icon style={{ fontSize: 20 }} />}
    </button>
  )
}

const Home = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [guardianConfirm, setGuardianConfirm] = useState(false)
  const [ageAttested, setAgeAttested] = useState(false)
  const [termsAccept, setTermsAccept] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isSignup, setIsSignup] = useState(false)
  const [signupRole, setSignupRole] = useState(null)
  const [isResetPassword, setIsResetPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [accessNotice, setAccessNotice] = useState(null)
  const { login, logout, signup, signupStudent, userProfile, refreshUserProfile, resetPassword } = useAuth()
  const history = useHistory()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const isMountedRef = useRef(true)
  const mountTimeRef = useRef(Date.now())

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search)
    const invite = params.get('invite')
    if (invite) rememberParentInviteToken(invite)
    if (params.get('signup') === 'parent') {
      setIsSignup(true)
      setSignupRole('parent')
    }
  }, [location.search])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    mountTimeRef.current = Date.now()
    return () => {
      trackFathomEvent(getTimeBucket(Date.now() - mountTimeRef.current))
    }
  }, [])

  // Sends supported roles to their dashboard and holds every other role on
  // Home with an explanatory notice. University roles keep their Firebase
  // account but are signed out because the product no longer serves them.
  const routeByRole = useCallback(async (role) => {
    const destination = routeForRole(role)
    if (destination) {
      history.replace(destination)
      return
    }

    setAccessNotice(isUniversityRole(role) ? UNIVERSITY_SUNSET_MESSAGE : UNSUPPORTED_ROLE_MESSAGE)
    try {
      await logout()
    } catch (error) {
      console.error('Error signing out unsupported role:', error)
    }
  }, [history, logout])

  // Handles sessions restored from a previous visit.
  useEffect(() => {
    if (!userProfile) return
    routeByRole(userProfile.role)
  }, [userProfile, routeByRole])

  // The profile document does not exist until provisioning finishes, and the
  // auth listener can cache that miss. Retry the server read, then leave the
  // signup screen. A reload is not required to reach the name step.
  const openProvisionedAccount = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const profile = await refreshUserProfile()
      if (profile && profile.role) {
        await routeByRole(profile.role)
        return
      }
      if (!isMountedRef.current || attempt === 2) return
      await new Promise((resolve) => setTimeout(resolve, 300))
    }
  }, [refreshUserProfile, routeByRole])

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8
    const hasNumber = /\d/.test(password)
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
    return hasMinLength && hasNumber && hasSpecialChar
  }

  // parentStudent accounts may sign in with a username, so the identifier is
  // resolved to its email by a callable that exposes nothing else.
  const resolveLoginEmail = async (identifier) => {
    if (identifier.includes('@')) return identifier

    const resolve = httpsCallable(functions, 'resolveLoginEmail')
    const result = await resolve({ username: identifier })
    return result.data.email
  }

  const handleSignup = async () => {
    if (!email || !password) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' })
      return
    }

    if (!validatePassword(password)) {
      enqueueSnackbar('Password must be at least 8 characters with 1 number and 1 special character', { variant: 'error' })
      return
    }

    if (!guardianConfirm || !termsAccept) {
      enqueueSnackbar('Please confirm you are a parent/legal guardian and accept the Privacy Policy and Terms of Service', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      await signup(email, password, { guardianConsent: guardianConfirm, termsAccepted: termsAccept })

      trackFathomEvent('home_signup_parent')
      enqueueSnackbar('Account created successfully! Please check your email for verification.', { variant: 'success' })

      await openProvisionedAccount()
    } catch (error) {
      if (isMountedRef.current) {
        enqueueSnackbar(error.message, { variant: 'error' })
      }
    }
    if (isMountedRef.current) {
      setLoading(false)
    }
  }

  const handleStudentSignup = async () => {
    if (!email || !password) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' })
      return
    }

    if (!validatePassword(password)) {
      enqueueSnackbar('Password must be at least 8 characters with 1 number and 1 special character', { variant: 'error' })
      return
    }

    if (!ageAttested) {
      enqueueSnackbar('Please confirm that you are 13 years of age or older', { variant: 'error' })
      return
    }

    if (!termsAccept) {
      enqueueSnackbar('Please accept the Privacy Policy and Terms of Service', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      await signupStudent(email, password, { ageAttested, termsAccepted: termsAccept })

      trackFathomEvent('home_signup_student')
      enqueueSnackbar('Account created successfully! Please check your email for verification.', { variant: 'success' })

      await openProvisionedAccount()
    } catch (error) {
      if (isMountedRef.current) {
        enqueueSnackbar(error.message, { variant: 'error' })
      }
    }
    if (isMountedRef.current) {
      setLoading(false)
    }
  }

  const returnToRoleStep = () => {
    if (loading) return
    setEmail('')
    setPassword('')
    setShowPassword(false)
    setGuardianConfirm(false)
    setAgeAttested(false)
    setTermsAccept(false)
    const params = new URLSearchParams(location.search)
    if (params.get('invite') || params.get('signup') === 'parent') {
      setIsSignup(false)
      setSignupRole(null)
      return
    }
    setSignupRole(null)
  }

  const leaveSignup = () => {
    setAccessNotice(null)
    setIsSignup(false)
    setSignupRole(null)
    setGuardianConfirm(false)
    setAgeAttested(false)
    setTermsAccept(false)
  }

  const handleLogin = async () => {
    if (!email || !password) {
      enqueueSnackbar('Please fill in all fields', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      let loginEmail
      try {
        loginEmail = await resolveLoginEmail(email.trim())
      } catch (error) {
        if (isMountedRef.current) {
          enqueueSnackbar(
            error.code === 'functions/not-found' ? 'Username not found' : 'Unable to sign in. Please try again.',
            { variant: 'error' }
          )
          setLoading(false)
        }
        return
      }

      const result = await login(loginEmail, password)
      trackFathomEvent('home_signin')

      const profileDoc = await getDoc(doc(db, 'users', result.user.uid))
      const userRole = profileDoc.exists() ? profileDoc.data().role : null

      if (routeForRole(userRole) && isMountedRef.current) {
        enqueueSnackbar('Signed in successfully', { variant: 'success' })
      }

      await routeByRole(userRole)
    } catch (error) {
      if (isMountedRef.current) {
        enqueueSnackbar(error.message, { variant: 'error' })
      }
    }
    if (isMountedRef.current) {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setAccessNotice(null)
    if (isSignup && signupRole === 'student') {
      await handleStudentSignup()
    } else if (isSignup) {
      await handleSignup()
    } else {
      await handleLogin()
    }
  }

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      enqueueSnackbar('Please enter your email address', { variant: 'error' })
      return
    }

    try {
      setLoading(true)
      await resetPassword(resetEmail)
      if (isMountedRef.current) {
        enqueueSnackbar('Password reset email sent! Check your inbox.', { variant: 'success' })
        setIsResetPassword(false)
        setResetEmail('')
      }
    } catch (error) {
      if (isMountedRef.current) {
        enqueueSnackbar(error.message, { variant: 'error' })
      }
    }
    if (isMountedRef.current) {
      setLoading(false)
    }
  }

  return (
    <div className='home-container dynamic-bg'>
      <div className='home-theme-toggle'>
        <ThemeToggle />
      </div>

      <div className='home-layout'>
        <div className='signin-card'>
          <div className='logo-header'>
            <div className='logo-main-line'>
              <img src='/logo.svg' alt='Quillnote Logo' className='logo-icon' />
              <span className='logo-text brand-text'>Quillnote</span>
            </div>
          </div>

          {accessNotice && (
            <div className='home-access-notice' role='alert'>
              {accessNotice}
            </div>
          )}

          {!isSignup && !isResetPassword && (
            <p className='home-welcome-text'>Welcome back! Sign in to continue.</p>
          )}
          {isSignup && !signupRole && (
            <p className='home-welcome-text' id='signup-role-heading'>Who is creating this account?</p>
          )}
          {isSignup && signupRole === 'parent' && (
            <p className='home-welcome-text'>Create your parent account to get started.</p>
          )}
          {isSignup && signupRole === 'student' && (
            <p className='home-welcome-text'>Create your student account to get started.</p>
          )}

          {isResetPassword ? (
            <div className='reset-password-form'>
              <h3 className='reset-password-title'>Reset Password</h3>
              <p className='reset-password-description'>
                Enter your email address and we'll send you a reset link.
              </p>
              <input
                type='email'
                placeholder='Email address'
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className='signin-input'
                disabled={loading}
                required
              />
              <button
                type='button'
                className='signin-button'
                disabled={loading}
                onClick={handlePasswordReset}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          ) : isSignup && !signupRole ? (
            <div className='signup-role-step' role='group' aria-labelledby='signup-role-heading'>
              <button
                type='button'
                className='signin-button'
                onClick={() => setSignupRole('parent')}
              >
                I'm a parent
              </button>
              <button
                type='button'
                className='signin-button'
                onClick={() => setSignupRole('student')}
              >
                I'm a student
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={isSignup ? 'signup-form' : 'signin-form'}>
              {isSignup && (
                <button
                  type='button'
                  className='signup-back'
                  onClick={returnToRoleStep}
                  disabled={loading}
                >
                  Back
                </button>
              )}
              <input
                type={isSignup ? 'email' : 'text'}
                placeholder={isSignup ? 'Email' : 'Email or Username'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className='signin-input'
                disabled={loading}
                required
              />

              <div className='password-field-container'>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={isSignup ? 'Password (min 8 chars, 1 number, 1 special)' : 'Password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='signin-input'
                  disabled={loading}
                  required
                />
                <button
                  type='button'
                  className={`password-toggle ${showPassword ? 'visible' : ''}`}
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? 'hide' : 'show'}
                </button>
              </div>

            {isSignup && signupRole === 'parent' && (
              <>
                <div className='checkbox-container'>
                  <label className='checkbox-label'>
                    <input
                      type='checkbox'
                      checked={guardianConfirm}
                      onChange={(e) => setGuardianConfirm(e.target.checked)}
                      disabled={loading}
                      required
                    />
                    I confirm that I am a parent or legal guardian.
                  </label>
                </div>

                <div className='checkbox-container'>
                  <label className='checkbox-label'>
                    <input
                      type='checkbox'
                      checked={termsAccept}
                      onChange={(e) => setTermsAccept(e.target.checked)}
                      disabled={loading}
                      required
                    />
                    I accept the{' '}
                    <span
                      className='policy-link'
                      onClick={() => window.open('/privacy-policy', '_blank')}
                    >
                      Privacy Policy
                    </span>
                    {' '}and{' '}
                    <span
                      className='policy-link'
                      onClick={() => window.open('/terms-of-service', '_blank')}
                    >
                      Terms of Service
                    </span>
                  </label>
                </div>
              </>
            )}

            {isSignup && signupRole === 'student' && (
              <>
                <div className='checkbox-container'>
                  <label className='checkbox-label'>
                    <input
                      type='checkbox'
                      checked={ageAttested}
                      onChange={(e) => setAgeAttested(e.target.checked)}
                      disabled={loading}
                    />
                    I confirm that I am 13 years of age or older.
                  </label>
                </div>
                <p className='signup-age-note'>If you are under 13, a parent needs to create your account.</p>

                <div className='checkbox-container'>
                  <label className='checkbox-label'>
                    <input
                      type='checkbox'
                      checked={termsAccept}
                      onChange={(e) => setTermsAccept(e.target.checked)}
                      disabled={loading}
                    />
                    I accept the{' '}
                    <span
                      className='policy-link'
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        window.open('/privacy-policy', '_blank', 'noopener,noreferrer')
                      }}
                    >
                      Privacy Policy
                    </span>
                    {' '}and{' '}
                    <span
                      className='policy-link'
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        window.open('/terms-of-service', '_blank', 'noopener,noreferrer')
                      }}
                    >
                      Terms of Service
                    </span>
                  </label>
                </div>
              </>
            )}

            <button
                type='submit'
                className='signin-button'
                disabled={loading}
              >
                {loading ? (isSignup ? 'Creating Account...' : 'Signing In...') : (isSignup ? 'Create Account' : 'Sign In')}
              </button>
            </form>
          )}

          {!isResetPassword && (
            <div className='signup-link'>
              {isSignup ? 'Already have an account? ' : "Don't have an account? "}
              <span className='create-account' onClick={() => {
                if (isSignup) {
                  leaveSignup()
                  return
                }
                setAccessNotice(null)
                setSignupRole(null)
                setIsSignup(true)
              }}>
                {isSignup ? 'Sign in.' : 'Create one.'}
              </span>
            </div>
          )}

          {isResetPassword && (
            <div className='signup-link'>
              <span className='create-account' onClick={() => setIsResetPassword(false)}>
                Back to Sign In
              </span>
            </div>
          )}

          {!isSignup && !isResetPassword && (
            <div className='reset-password-container'>
              <div className='reset-password-link' onClick={() => setIsResetPassword(true)}>
                Forgot password?
              </div>
            </div>
          )}
        </div>
        <BetaVersionBadge size='lg' />
      </div>
    </div>
  )
}

export default Home
